// ─────────────────────────────────────────────────────────────
// Measurement Protocol — TAB 4: COMMUNICATION / RESULT
//
// Moved verbatim out of the original monolithic MPCreationIndex.js.
// Mandatory fields live in the "Monitoring & Results" panel, which is
// collapsed by default — so validate() force-opens that panel (via a
// controlled Section) before focusing the offending field.
//
// VALIDATION — Communication Setup grid: optional overall, but the
// moment one row exists, Com Name / Com Type / Near PC / Near DUT are
// all mandatory on it. See `checkComRows()` below.
//
// VALIDATION — Publishing Status: Photos Published and Data File
// Published are always mandatory (same as Server Availability).
//
// VALIDATION — Test Results grid: Test Name, DUT, Result and Updated
// By are always mandatory on every row (regardless of Smile
// integration), and at least one row is always required. See
// `checkResultRows()` below; "Test Results" is also a controlled,
// collapsible Section so Submit can force it open when it's hiding an
// invalid row.
// ─────────────────────────────────────────────────────────────
import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useDispatch } from "react-redux";

import {
  toArray,
  iCls,
  eiCls,
  iconInputCls,
  FLbl,
  IconField,
  Section,
  AutoComplete,
  PortalDropdown,
  ToggleSwitch,
  SegmentedSelect,
  Btn,
  TD,
  Empty,
  EmailPreviewModal,
  Toast,
  FormActions,
} from "../shared/mpShared";
import {
  getCompanyID,
  ConfirmDialog,
  getEmployeeID,
  hasPrivilege,
  isSmileIntegrated,
  PRIVILEGES,
  Textarea,
} from "../../../Common/UIComponents";
import {
  MP_DeleteMPDetails,
  MP_GetCommunicationChainDetails,
  MP_SaveCommunication,
} from "../../../API_Service/MeasurementProtocol_Slice";

const CommunicationResultTab = forwardRef(function CommunicationResultTab(
  { mpID, mp, resultList = [], onSaved, onSubmitAll },
  ref,
) {
  const dispatch = useDispatch();
  const blank = () => ({
    _id: Date.now() + Math.random(),
    comName: "",
    comType: "",
    nearPC: "",
    nearDUT: "",
  });
  const [comRows, setComRows] = useState([]);
  const [form, setForm] = useState({
    monitoringDetails: "",
    monitoringSWVer: "",
    preCheck: "",
    photosPublished: "",
    dataFilePublished: "",
    serverAvail: "",
    observations: "",
    remarks: "",
  });
  const [errs, setErrs] = useState({});
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const fieldRefs = useRef({});
  const [communicationTypeOptions, setCommunicationTypeOptions] = useState([]);
  const [neerPCOptions, setNeerPCOptions] = useState([]);
  const [neerDUTOptions, setNeerDUTOptions] = useState([]);
  const [testNameOptions, setTestNameOptions] = useState([]);
  const [dutRefOptions, setDutRefOptions] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  // Communication Setup: rows are optional overall, but the moment one
  // exists every field on it becomes mandatory.
  // Row-level validation: { [row._id]: { comName, comType, nearPC, nearDUT } }
  const [comRowErrs, setComRowErrs] = useState({});
  const [comRowsError, setComRowsError] = useState("");
  const comTableRef = useRef(null);

  const notify = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 2500);
  };

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrs((e) => ({ ...e, [k]: undefined }));
  };
  const addCom = () => {
    setComRows((r) => [...r, blank()]);
    setComRowsError("");
  };
  const removeCom = async () => {
    const { row } = deleteDialog;
    const id = row._id;
    if (!id) return;
    const isTempId = typeof id === "number" && id > 1000000000000;
    if (isTempId) {
      setComRows((r) => r.filter((x) => x._id !== id));
      setDeleteDialog({ open: false, row: null });
      notify("Communication Deleted Successfully");
      return;
    }
    try {
      const payload = {
        id: id,
        code: "CAN",
        modifiedBy: getEmployeeID(),
      };
      const res = await dispatch(MP_DeleteMPDetails(payload));

      if (res?.payload?.data > 0) {
        setComRows((r) => r.filter((x) => x._id !== id));
        setDeleteDialog({ open: false, row: null });
        notify("Communication Deleted Successfully");
      } else {
        notify("Couldn't remove Communication. Please try again.", "error");
      }
    } catch (err) {
      console.error("Error deleting Communication:", err);
      notify("An error occurred while removing the Communication.", "error");
    }
  };
  const updCom = (id, k, v) => {
    setComRows((r) => r?.map((x) => (x._id === id ? { ...x, [k]: v } : x)));
    setComRowErrs((e) => {
      if (!e[id]) return e;
      return { ...e, [id]: { ...e[id], [k]: undefined } };
    });
    setComRowsError("");
  };

  const blankResult = () => ({
    _id: Date.now() + Math.random(),
    TestName: "",
    DUT: "",
    OperatingMode: "",
    RequiredClass: "",
    AchievedClass: "",
    Result: "",
    UpdatedBy: getEmployeeID(),
    WitnessedBy: "",
  });
  const [resultRows, setResultRows] = useState(() => {
    const mapped = (resultList ?? [])?.map((r) => ({
      _id: Date.now() + Math.random(),
      ...r,
    }));
    // A Test Result row is always required, so seed one blank row when
    // nothing has been logged yet — matches the Measurement Chain / DUT
    // Chain tabs, which always start with at least one row.
    return mapped.length > 0 ? mapped : [blankResult()];
  });
  // Row-level validation: { [row._id]: { TestName, DUT, Result, UpdatedBy } }
  const [resultErrs, setResultErrs] = useState({});
  // Table-level validation message, e.g. "at least one row is required"
  const [resultRowsError, setResultRowsError] = useState("");
  // "Test Results" is a collapsible Section — controlled so Submit can
  // force it open when it's the tab hiding the invalid row(s).
  const [resultsOpen, setResultsOpen] = useState(false);
  const resultsTableRef = useRef(null);

  const addResult = () => {
    setResultRows((r) => [...r, blankResult()]);
    setResultRowsError("");
  };
  const removeResult = (id) =>
    setResultRows((r) => r.filter((x) => x._id !== id));
  const updResult = (id, k, v) => {
    setResultRows((r) => r?.map((x) => (x._id === id ? { ...x, [k]: v } : x)));
    setResultErrs((e) => {
      if (!e[id]) return e;
      return { ...e, [id]: { ...e[id], [k]: undefined } };
    });
    setResultRowsError("");
  };
  const RESULT_OPTS = [
    { value: "Pass", label: "Pass" },
    { value: "Fail", label: "Fail" },
    { value: "See Observation", label: "See Observation" },
  ];

  const SERVER_AVAIL_OPTS = [
    { value: "Data Available", label: "Data Available" },
    {
      value: "Data Not Available (Ref Remarks)",
      label: "Data Not Available (Ref Remarks)",
    },
    {
      value: "Partial Data Available (Ref Remarks)",
      label: "Partial Data Available (Ref Remarks)",
    },
  ];

  // Mandatory fields marked `required` on this tab's UI.
  const MONITORING_FIELDS = [
    "monitoringDetails",
    "monitoringSWVer",
    "preCheck",
    "observations",
  ];

  // Server Availability is always required. The Monitoring & Results
  // fields become mandatory ONLY when this app is running embedded
  // inside Smile (isSmileIntegrated()) — same convention already used
  // for the Calibration/Verification Due Date column on the Instrument
  // table and for the Test Name field on the Test Info tab — and are
  // left optional when running standalone. Returns the error map so
  // both the Save button and the cross-tab Submit flow can reuse the
  // same rule set.
  const validateFields = () => {
    const e = {};

    if (isSmileIntegrated()) {
      if (!form.serverAvail) e.serverAvail = "Server Availability is Required";
      if (!form.photosPublished)
        e.photosPublished = "Photos Published is Required";
      if (!form.dataFilePublished)
        e.dataFilePublished = "Data File Published is Required";
      if (!form.monitoringDetails?.trim())
        e.monitoringDetails = "Monitoring Details is Required";
      if (!form.monitoringSWVer?.trim())
        e.monitoringSWVer = "Monitoring S/W Version is Required";
      if (!form.preCheck) e.preCheck = "Pre Check is Required";
      if (!form.observations?.trim())
        e.observations = "Observations is Required";
    }
    return e;
  };

  const getPreviewRows = () => [
    { label: "Communication Rows", value: `${comRows?.length}` },
    { label: "Monitoring Details", value: form.monitoringDetails },
    { label: "Monitoring S/W Version", value: form.monitoringSWVer },
    { label: "Pre Check", value: form.preCheck },
    { label: "Photos Published", value: form.photosPublished },
    { label: "Data File Published", value: form.dataFilePublished },
    { label: "Server Availability", value: form.serverAvail },
    { label: "Observations", value: form.observations },
    { label: "Test Results Logged", value: `${resultRows?.length}` },
  ];

  useEffect(() => {
    GetCommunicationChainDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GetCommunicationChainDetails = () => {
    const payload = {
      mpid: mpID,
      companyID: getCompanyID(),
    };
    dispatch(MP_GetCommunicationChainDetails(payload))
      .then((res) => {
        if (res?.payload?.data) {
          const {
            CanTableData,
            CommData,
            NeerPCDUT,
            CommunicationType,
            DUTRefList,
            TestList,
            TestResult,
            EmployeeList,
          } = res.payload.data;
          const commRow = toArray(CommData)[0] ?? {};

          setForm({
            monitoringDetails: commRow?.Monitoring_Details ?? "",
            monitoringSWVer: commRow?.Monitoring_Sw_Version ?? "",
            preCheck: commRow?.Pre_Check ?? "",
            photosPublished: commRow?.Photo_Publilsh ?? "",
            dataFilePublished: commRow?.Data_File_Published ?? "",
            serverAvail: commRow?.Server_Data_Availability ?? "",
            observations: commRow?.Observation ?? "",
            remarks: commRow?.Remarks ?? "",
            MPCANDetailsID: commRow?.MP_CAN_Details_Id,
          });

          const mappedResults = toArray(TestResult)?.map((t) => ({
            _id: t?.MP_DUT_Det_ID,
            TestName: t?.Project_Test_Det_Id,
            DUT: t?.DUT_Ref_No,
            OperatingMode: t?.Operating_Mode,
            RequiredClass: t?.Required_Class,
            AchievedClass: t?.Achieved_Class,
            Result: t?.Result,
            UpdatedBy: t?.Tested_By ? t?.Tested_By : getEmployeeID(),
            WitnessedBy: t?.Witnessed_By,
          }));
          // A Test Result row is always required — fall back to one
          // blank row instead of leaving the table empty.
          setResultRows(mappedResults);

          setComRows(
            toArray(CanTableData)?.map((c) => ({
              _id: c?.MP_CAN_Id,
              comName: c?.Communication_Name,
              comType: c?.Communication_Type
                ? { value: c.Communication_Type, label: c.Communication_Type }
                : null,
              nearPC: c?.CAN_NEAR_PC
                ? { value: c.CAN_NEAR_PC, label: c.CAN_NEAR_PC }
                : null,
              nearDUT: c?.CAN_NEAR_DUT
                ? { value: c.CAN_NEAR_DUT, label: c.CAN_NEAR_DUT }
                : null,
            })),
          );

          setCommunicationTypeOptions(
            toArray(CommunicationType)?.map((c) => ({
              label: c?.Communication_Type,
              value: c?.Communication_Type_Id,
            })),
          );
          const pcdutOptions = toArray(NeerPCDUT).map((n) => ({
            label: n?.Near_PC_DUT,
            value: n?.Near_PC_DUT_Id,
          }));

          setNeerPCOptions(pcdutOptions);
          setNeerDUTOptions(pcdutOptions);

          setDutRefOptions(
            DUTRefList?.map((d) => ({
              label: d.DUT_Ref_No,
              value: d.DUT_Ref_No,
            })),
          );

          setTestNameOptions(
            TestList?.map((t) => ({
              label: t?.Test_Name,
              value: t?.Project_Test_Det_Id,
            })),
          );

          setEmployeeList(
            EmployeeList?.map((e) => ({
              label: e?.Employeename,
              value: e?.Employee_Id,
            })),
          );
        }
      })
      .catch((err) => {
        console.error("Error fetching communication chain details:", err);
        notify(
          "Couldn't load communication/result details. Please refresh.",
          "error",
        );
      });
  };

  // Communication Setup: the grid itself is optional (no "at least one
  // row" rule), but the moment a row exists, every field on it —
  // Com Name, Com Type, Near PC, Near DUT — becomes mandatory.
  const checkComRows = () => {
    if (!comRows || comRows.length === 0) {
      return { rowErrs: {}, rowsError: "" };
    }
    const rowErrs = {};
    comRows.forEach((r) => {
      const rowErr = {};
      if (!r.comName?.trim()) rowErr.comName = " Com Name is Required";
      if (!r.comType) rowErr.comType = " Com Type is Required";
      if (!r.nearPC) rowErr.nearPC = " Near PC is Required";
      if (!r.nearDUT) rowErr.nearDUT = " Near DUT is Required";
      if (Object.keys(rowErr).length) rowErrs[r._id] = rowErr;
    });
    return {
      rowErrs,
      rowsError: Object.keys(rowErrs).length
        ? "Com Name, Com Type, Near PC and Near DUT are required for every Communication Setup row."
        : "",
    };
  };

  // Test Results: Test Name, DUT, Result and Updated By are always
  // mandatory on every row, and at least one row is always required —
  // regardless of Smile integration.
  const checkResultRows = () => {
    if (!resultRows || (resultRows.length === 0 && isSmileIntegrated())) {
      return {
        rowErrs: {},
        rowsError: "At least one test result row is required.",
      };
    }
    const rowErrs = {};
    resultRows.forEach((r) => {
      const rowErr = {};
      if (!r.TestName) rowErr.TestName = " Test Name is Required";
      if (!r.DUT) rowErr.DUT = " DUT is Required";
      if (!r.Result) rowErr.Result = " Result is Required";
      if (!r.UpdatedBy) rowErr.UpdatedBy = " Updated By is Required";
      if (Object.keys(rowErr).length) rowErrs[r._id] = rowErr;
    });
    return {
      rowErrs,
      rowsError: Object.keys(rowErrs).length
        ? "Test Name, DUT, Result and Updated By are required for every test result row."
        : "",
    };
  };

  const handleSave = () =>
    new Promise((resolve, reject) => {
      const e = validateFields();
      const { rowErrs: comErrs, rowsError: comErr } = checkComRows();
      const { rowErrs, rowsError: rErr } = checkResultRows();
      setComRowErrs(comErrs);
      setComRowsError(comErr);
      setResultErrs(rowErrs);
      setResultRowsError(rErr);
      if (rErr) setResultsOpen(true);
      if (Object.keys(e).length || comErr || rErr) {
        setErrs(e);
        notify(
          comErr && !Object.keys(e).length
            ? comErr
            : rErr && !Object.keys(e).length
              ? rErr
              : "Please fill in the required fields.",
          "error",
        );
        // reject(new Error("Validation failed"));
        return;
      }
      try {
        var CommunicationCanDetails = [];
        var CommunicationDutResultDetails = [];

        comRows.map((c) => {
          const Can = {
            MPCanId:
              typeof c?._id === "number" && c?._id > 1000000000000 ? 0 : c?._id,
            CommunicationName: c?.comName,
            CommunicationType: c?.comType?.label,
            CanNearPC: c?.nearPC?.label,
            CanNearDUT: c?.nearDUT?.label,
          };
          CommunicationCanDetails.push(Can);
        });

        resultRows.map((r) => {
          const dutResult = {
            MPDutDetID:
              typeof r?._id === "number" && r?._id > 1000000000000 ? 0 : r?._id,
            TestedBy: r?.UpdatedBy,
            WitnessedBy: r?.WitnessedBy,
            DUTRefNo: r?.DUT,
            RequiredClass: r?.RequiredClass,
            Result: r?.Result,
            AchievedClass: r?.AchievedClass,
            OperatingMode: r?.OperatingMode,
            TestName: r?.TestName
              ? testNameOptions.filter((t) => t.value == r?.TestName)[0]?.label
              : null,
            ProjectTestDetId: r?.TestName,
          };
          CommunicationDutResultDetails.push(dutResult);
        });

        const payload = {
          MPID: mpID,
          MPCANDetailsID: form?.MPCANDetailsID,
          PreCheck: form?.preCheck,
          MonitoringDetails: form?.monitoringDetails,
          MonitoringSwVersion: form?.monitoringSWVer,
          DataFilePublished: form?.dataFilePublished,
          PhotoPublilsh: form?.photosPublished,
          Observation: form?.observations,
          Remarks: form?.remarks,
          ServerDataAvailability: form?.serverAvail,
          CompanyID: getCompanyID(),
          SavedBy: getEmployeeID(),
          CommunicationCanDetails: CommunicationCanDetails,
          CommunicationDutResultDetails: CommunicationDutResultDetails,
        };

        dispatch(MP_SaveCommunication(payload))
          .then((res) => {
            if (res?.payload?.data) {
              const { CanRes, Commres, dutres } = res?.payload?.data;
              if (CanRes > 0 || Commres > 0 || dutres > 0) {
                setSaving(true);
                GetCommunicationChainDetails();
                setTimeout(() => {
                  setSaving(false);
                  notify("Communication/Result saved!");
                  setTimeout(() => {
                    onSaved?.();
                    resolve(true);
                  }, 800);
                }, 800);
              } else {
                notify("Couldn't save Communication/Result.", "error");
                reject(new Error("Save returned no rows affected"));
              }
            } else {
              notify("Couldn't save Communication/Result.", "error");
              reject(new Error("Save returned no data"));
            }
          })
          .catch((err) => {
            console.error("Error saving Communication/Result:", err);
            setSaving(false);
            notify("An error occurred while saving.", "error");
            reject(err);
          });
      } catch (err) {
        console.error("Error saving Communication/Result:", err);
        setSaving(false);
        notify("An error occurred while saving.", "error");
        reject(err);
      }
    });

  // Imperative API used by MPExpandedRow's cross-tab Submit flow — same
  // contract as TestInfoTab's `validate`, so Submit checks this tab's
  // required fields (Server Availability always, Monitoring & Results
  // fields only when Smile-integrated, Test Results grid always)
  // instead of skipping it.
  const validate = () => {
    const e = validateFields();
    setErrs(e);
    const { rowErrs: comErrs, rowsError: comErr } = checkComRows();
    setComRowErrs(comErrs);
    setComRowsError(comErr);
    const { rowErrs, rowsError: rErr } = checkResultRows();
    setResultErrs(rowErrs);
    setResultRowsError(rErr);
    const firstKey = Object.keys(e)[0];
    const valid = Object.keys(e).length === 0 && !comErr && !rErr;
    return {
      valid,
      field:
        firstKey || (comErr ? "comRows" : null) || (rErr ? "resultRows" : null),
    };
  };

  const focusField = (field) => {
    if (MONITORING_FIELDS.includes(field)) setMonitoringOpen(true);
    if (field === "comRows") {
      setTimeout(() => {
        comTableRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
      }, 80);
      return;
    }
    if (field === "resultRows") {
      setResultsOpen(true);
      setTimeout(() => {
        resultsTableRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
      }, 80);
      return;
    }
    setTimeout(() => {
      const node = fieldRefs.current[field];
      if (node) {
        node.focus?.();
        node.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }
    }, 80);
  };

  useImperativeHandle(ref, () => ({
    validate,
    focusField,
    getPreviewRows,
    save: handleSave,
  }));

  const checkOpts = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "NA", label: "Not Applicable" },
  ];

  const handleDelete = useCallback((row) => {
    setDeleteBlocked(false);
    setDeleteDialog({ open: true, row });
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false, row: null });
    setDeleteBlocked(false);
  }, []);

  return (
    <>
      <Toast msg={toast.msg} show={toast.show} type={toast.type} />
      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        mp={mp}
        subject={`Communication & Result Update — ${mp?.MPNo ?? "New MP"}`}
        rows={getPreviewRows()}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start min-w-0">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <Section
            icon="cable"
            title="Communication Setup"
            action={
              <div className="flex items-center gap-3">
                {hasPrivilege(PRIVILEGES.MP_CREATE) && (
                  <button
                    onClick={addCom}
                    className="flex items-center gap-1.5 text-[#08757C] text-[12px] font-bold hover:underline"
                  >
                    <span className="material-symbols-outlined text-[15px]">
                      add
                    </span>
                    Add
                  </button>
                )}
              </div>
            }
          >
            {comRowsError && (
              <p className="text-[11px] font-semibold text-red-500 mb-2 flex items-center gap-1">
                <span className="material-symbols-outlined text-[13px]">
                  error
                </span>
                {comRowsError}
              </p>
            )}
            <div
              ref={comTableRef}
              className="rounded-xl border border-[#e5e9e9] w-full max-w-full overflow-x-auto"
            >
              <table className="w-full min-w-[760px] border-collapse text-[11.5px]">
                <thead>
                  <tr>
                    {[
                      "S.No",
                      "Com Name",
                      "Com Type",
                      "Near PC",
                      "Near DUT",
                      ...(hasPrivilege(PRIVILEGES.MP_DELETE)
                        ? ["Options"]
                        : []),
                    ]?.map((h) => (
                      <th
                        key={h}
                        className="bg-[#f1f4f4] px-4 py-3 text-left text-[#3e494a] font-bold text-[11px] uppercase tracking-wider whitespace-nowrap border-b border-[#ebeeee]"
                      >
                        {h}
                        {comRows.length > 0 &&
                          [
                            "Com Name",
                            "Com Type",
                            "Near PC",
                            "Near DUT",
                          ].includes(h) && (
                            <span className="text-red-500 ml-0.5">*</span>
                          )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comRows?.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <Empty
                          icon="cable"
                          msg="No records"
                          sub="Click Add to insert a row"
                        />
                      </td>
                    </tr>
                  ) : (
                    comRows?.map((row, idx) => (
                      <tr
                        key={row._id}
                        className={idx % 2 === 0 ? "bg-white" : "bg-[#fafcfc]"}
                      >
                        <TD cls="text-center font-bold text-[#c0cbcc] w-10">
                          {idx + 1}
                        </TD>
                        <TD>
                          <input
                            className={`${eiCls} ${comRowErrs[row._id]?.comName ? "border-2 border-red-500" : "border border-slate-200"}`}
                            value={row.comName}
                            onChange={(e) =>
                              updCom(row._id, "comName", e.target.value)
                            }
                            placeholder="Com name"
                          />
                          {comRowErrs[row._id]?.comName && (
                            <p className="text-xs text-red-500 mt-1">
                              {comRowErrs[row._id].comName}
                            </p>
                          )}
                        </TD>
                        <TD style={{ minWidth: 160 }}>
                          <AutoComplete
                            value={row.comType}
                            onChange={(v) => updCom(row._id, "comType", v)}
                            options={communicationTypeOptions}
                            placeholder="Select"
                            error={comRowErrs[row._id]?.comType}
                          />
                          {comRowErrs[row._id]?.comType && (
                            <p className="text-xs text-red-500 mt-1">
                              {comRowErrs[row._id].comType}
                            </p>
                          )}
                        </TD>
                        <TD style={{ minWidth: 170 }}>
                          <AutoComplete
                            value={row.nearPC}
                            onChange={(v) => updCom(row._id, "nearPC", v)}
                            options={neerPCOptions}
                            placeholder="Select"
                            error={comRowErrs[row._id]?.nearPC}
                          />
                          {comRowErrs[row._id]?.nearPC && (
                            <p className="text-xs text-red-500 mt-1">
                              {comRowErrs[row._id].nearPC}
                            </p>
                          )}
                        </TD>
                        <TD style={{ minWidth: 170 }}>
                          <AutoComplete
                            value={row.nearDUT}
                            onChange={(v) => updCom(row._id, "nearDUT", v)}
                            options={neerDUTOptions}
                            placeholder="Select"
                            error={comRowErrs[row._id]?.nearDUT}
                          />
                          {comRowErrs[row._id]?.nearDUT && (
                            <p className="text-xs text-red-500 mt-1">
                              {comRowErrs[row._id].nearDUT}
                            </p>
                          )}
                        </TD>
                        {hasPrivilege(PRIVILEGES.MP_DELETE) && (
                          <TD cls="text-center">
                            <Btn
                              icon="delete"
                              onClick={() => handleDelete(row)}
                              danger
                            />
                          </TD>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        <div className="space-y-4 min-w-0">
          <Section icon="cloud_sync" title="Publishing Status">
            <div className="divide-y divide-[#f1f4f4]">
              <div
                ref={(el) => (fieldRefs.current.photosPublished = el)}
                className={
                  errs.photosPublished
                    ? "ring-2 ring-red-500 rounded-lg -mx-1 "
                    : ""
                }
              >
                <ToggleSwitch
                  label="Photos Published"
                  sub="Media Upload"
                  checked={form.photosPublished === "Yes"}
                  onChange={(v) => setF("photosPublished", v ? "Yes" : "No")}
                  required={isSmileIntegrated()}
                />
              </div>
              {errs.photosPublished && (
                <p className="text-xs text-red-500 mt-1">
                  {errs.photosPublished}
                </p>
              )}
              <div
                ref={(el) => (fieldRefs.current.dataFilePublished = el)}
                className={
                  errs.dataFilePublished
                    ? "ring-2 ring-red-500 rounded-lg -mx-1 "
                    : ""
                }
              >
                <ToggleSwitch
                  label="Data File Published"
                  sub="Raw Data"
                  checked={form.dataFilePublished === "Yes"}
                  onChange={(v) => setF("dataFilePublished", v ? "Yes" : "No")}
                  required={isSmileIntegrated()}
                />
              </div>
              {errs.dataFilePublished && (
                <p className="text-xs text-red-500 mt-1">
                  {errs.dataFilePublished}
                </p>
              )}

              <FLbl required={isSmileIntegrated()}>
                Check on server for all Availability
              </FLbl>
              <div ref={(el) => (fieldRefs.current.serverAvail = el)}>
                <PortalDropdown
                  value={form.serverAvail}
                  onChange={(v) => setF("serverAvail", v)}
                  options={SERVER_AVAIL_OPTS}
                  placeholder="Select availability"
                  error={errs.serverAvail}
                />
              </div>
            </div>
          </Section>
        </div>
      </div>

      <Section
        icon="monitor_heart"
        title="Monitoring & Results"
        collapsible
        open={monitoringOpen}
        onOpenChange={setMonitoringOpen}
        className="mt-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FLbl required={isSmileIntegrated()}>Monitoring Details</FLbl>
            <Textarea
              ref={(el) => (fieldRefs.current.monitoringDetails = el)}
              className={`${iCls} h-16 py-2.5 resize-none`}
              placeholder="Enter monitoring details…"
              value={form.monitoringDetails}
              onChange={(e) => setF("monitoringDetails", e.target.value)}
              error={errs.monitoringDetails}
            />
          </div>
          <div>
            <FLbl required={isSmileIntegrated()}>Monitoring S/W Version</FLbl>
            <IconField icon="tag">
              <input
                ref={(el) => (fieldRefs.current.monitoringSWVer = el)}
                className={`${iconInputCls} ${
                  errs.monitoringSWVer
                    ? "border-2 border-red-500"
                    : "border border-slate-200"
                }`}
                placeholder="e.g. v3.2.1"
                value={form.monitoringSWVer}
                onChange={(e) => setF("monitoringSWVer", e.target.value)}
              />
            </IconField>
          </div>
          <div ref={(el) => (fieldRefs.current.preCheck = el)}>
            <FLbl required={isSmileIntegrated()}>Pre Check</FLbl>

            <SegmentedSelect
              value={form.preCheck}
              onChange={(v) => setF("preCheck", v)}
              options={checkOpts}
              error={errs.preCheck}
            />
            {errs.preCheck && (
              <p className="text-xs text-red-500 mt-1">{errs.preCheck}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <FLbl required={isSmileIntegrated()}>Observations</FLbl>
            <Textarea
              ref={(el) => (fieldRefs.current.observations = el)}
              className={`${iCls} h-16 py-2.5 resize-none`}
              placeholder="Enter observations…"
              value={form.observations}
              onChange={(e) => setF("observations", e.target.value)}
              error={errs.observations}
            />
          </div>
          <div className="md:col-span-2">
            <FLbl>Remarks</FLbl>
            <textarea
              className={`${iCls} h-14 py-2.5 resize-none`}
              placeholder="Optional remarks…"
              value={form.remarks}
              onChange={(e) => setF("remarks", e.target.value)}
            />
          </div>
        </div>
      </Section>
      <Section
        icon="leaderboard"
        title="Test Results"
        collapsible
        open={resultsOpen}
        onOpenChange={setResultsOpen}
        className="mt-4"
        action={
          hasPrivilege(PRIVILEGES.MP_CREATE) &&
          !isSmileIntegrated() && (
            <button
              onClick={addResult}
              className="flex items-center gap-1.5 text-[#08757C] text-[12px] font-bold hover:underline"
            >
              <span className="material-symbols-outlined text-[15px]">add</span>
              Add
            </button>
          )
        }
      >
        {resultRowsError && (
          <p className="text-[11px] font-semibold text-red-500 mb-2 flex items-center gap-1">
            <span className="material-symbols-outlined text-[13px]">error</span>
            {resultRowsError}
          </p>
        )}
        <div
          ref={resultsTableRef}
          className="rounded-xl border border-[#e5e9e9] w-full max-w-full overflow-x-auto"
        >
          <table className="w-full min-w-[1180px] border-collapse text-[11.5px]">
            <thead>
              <tr>
                {[
                  { h: "S.No" },
                  { h: "Test Name", req: true },
                  { h: "DUT", req: true },
                  { h: "Operating Mode" },
                  { h: "Required Class" },
                  { h: "Achieved Class" },
                  { h: "Result", req: true },
                  { h: "Updated By", req: true },
                  { h: "Witnessed By" },
                  // "Options",
                ]?.map(({ h, req }) => (
                  <th
                    key={h}
                    className="bg-[#f1f4f4] px-4 py-3 text-left text-[#3e494a] font-bold text-[11px] uppercase tracking-wider whitespace-nowrap border-b border-[#ebeeee]"
                  >
                    {h}
                    {req && <span className="text-red-500 ml-0.5">*</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resultRows?.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <Empty
                      icon="assignment"
                      msg="No test results"
                      sub="Click Add to insert a result row"
                    />
                  </td>
                </tr>
              ) : (
                resultRows?.map((row, idx) => (
                  <tr
                    key={row._id}
                    className={idx % 2 === 0 ? "bg-white" : "bg-[#fafcfc]"}
                  >
                    <TD cls="text-center font-bold text-[#c0cbcc] w-10">
                      {idx + 1}
                    </TD>
                    <TD style={{ minWidth: 150 }}>
                      <PortalDropdown
                        options={testNameOptions}
                        value={row.TestName}
                        onChange={(e) => updResult(row._id, "TestName", e)}
                        placeholder="Test name"
                        disabled={
                          typeof row._id === "number" && row._id > 1000000000000
                            ? false
                            : true
                        }
                        error={resultErrs[row._id]?.TestName}
                      />
                    </TD>
                    <TD style={{ minWidth: 120 }}>
                      <PortalDropdown
                        options={dutRefOptions}
                        value={row.DUT}
                        onChange={(e) => updResult(row._id, "DUT", e)}
                        placeholder="DUT"
                        disabled={
                          typeof row._id === "number" && row._id > 1000000000000
                            ? false
                            : true
                        }
                        error={resultErrs[row._id]?.DUT}
                      />
                    </TD>
                    <TD style={{ minWidth: 160 }}>
                      <input
                        className={eiCls}
                        value={row.OperatingMode}
                        onChange={(e) =>
                          updResult(row._id, "OperatingMode", e.target.value)
                        }
                        placeholder="Operating mode"
                      />
                    </TD>
                    <TD style={{ minWidth: 120 }}>
                      <input
                        className={eiCls}
                        value={row.RequiredClass}
                        onChange={(e) =>
                          updResult(row._id, "RequiredClass", e.target.value)
                        }
                        placeholder="Required class"
                      />
                    </TD>
                    <TD style={{ minWidth: 120 }}>
                      <input
                        className={eiCls}
                        value={row.AchievedClass}
                        onChange={(e) =>
                          updResult(row._id, "AchievedClass", e.target.value)
                        }
                        placeholder="Achieved class"
                      />
                    </TD>
                    <TD style={{ minWidth: 160 }}>
                      <PortalDropdown
                        value={row.Result}
                        onChange={(v) => updResult(row._id, "Result", v)}
                        options={RESULT_OPTS}
                        placeholder="Select"
                        error={resultErrs[row._id]?.Result}
                      />
                    </TD>
                    <TD style={{ minWidth: 130 }}>
                      <div
                        className={
                          resultErrs[row._id]?.UpdatedBy
                            ? "border-2 border-red-500 rounded-lg"
                            : "border border-slate-200 rounded-lg"
                        }
                      >
                        <PortalDropdown
                          value={row.UpdatedBy}
                          onChange={(e) => updResult(row._id, "UpdatedBy", e)}
                          placeholder="Updated by"
                          options={employeeList}
                          disabled={true}
                        />
                      </div>
                      {resultErrs[row._id]?.UpdatedBy && (
                        <p className="text-xs text-red-500 mt-1">
                          {resultErrs[row._id].UpdatedBy}
                        </p>
                      )}
                    </TD>
                    <TD style={{ minWidth: 130 }}>
                      <input
                        className={eiCls}
                        value={row.WitnessedBy}
                        onChange={(e) =>
                          updResult(row._id, "WitnessedBy", e.target.value)
                        }
                        placeholder="Witnessed by"
                      />
                    </TD>
                    {/* <TD cls="text-center">
                      <Btn
                        icon="delete"
                        onClick={() => removeResult(row._id)}
                        danger
                        title="Remove"
                      />
                    </TD> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>
      {hasPrivilege(PRIVILEGES.MP_EDIT) && (
        <FormActions
          onSave={handleSave}
          saving={saving}
          onPreview={() => setPreviewOpen(true)}
          onSubmit={onSubmitAll}
        />
      )}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        title="Delete Communication"
        message={`Are you sure you want to delete "${deleteDialog.row?.comName || "Communication"}"? This action cannot be undone.`}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={removeCom}
        onCancel={handleCancelDelete}
      />
    </>
  );
});

// ─────────────────────────────────────────────────────────────

export default CommunicationResultTab;
export { CommunicationResultTab };
